import React, { useEffect, useState } from 'react';
import { FaEye } from '@react-icons/all-files/fa/FaEye';
import { FaSpinner } from '@react-icons/all-files/fa/FaSpinner';
import ProfaseModal from '../commons/ProfaseModal';
import { fetchProfaseArchivoContenido, type IProfaseArchivo } from './profase-archivo-api';
import './image-viewer-modal.css';

interface ImageViewerModalProps {
  open: boolean;
  archivo: IProfaseArchivo | null;
  onClose: () => void;
  fetchContent?: () => Promise<Blob>;
}

export default function ImageViewerModal({ open, archivo, onClose, fetchContent }: ImageViewerModalProps) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [imageUrl, setImageUrl] = useState<string | null>(null);

  useEffect(() => {
    if (!open || (!fetchContent && archivo?.id == null)) {
      setImageUrl((current) => {
        if (current) {
          URL.revokeObjectURL(current);
        }
        return null;
      });
      setError(null);
      setLoading(false);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError(null);
    setImageUrl((current) => {
      if (current) {
        URL.revokeObjectURL(current);
      }
      return null;
    });

    void (async () => {
      try {
        const blob = fetchContent
          ? await fetchContent()
          : (await fetchProfaseArchivoContenido(archivo!.id!, archivo!.nombre)).blob;
        if (cancelled) {
          return;
        }
        setImageUrl(URL.createObjectURL(blob));
      } catch {
        if (!cancelled) {
          setError('No se pudo cargar la imagen.');
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [open, archivo?.id, archivo?.nombre, fetchContent]);

  useEffect(() => () => {
    setImageUrl((current) => {
      if (current) {
        URL.revokeObjectURL(current);
      }
      return null;
    });
  }, []);

  return (
    <ProfaseModal
      open={open}
      titleId="image-viewer-modal-title"
      size="xlarge"
      scroll
      dialogClassName="image-viewer-modal"
      onBackdropMouseDown={(e) => {
        if (e.target === e.currentTarget) {
          onClose();
        }
      }}
    >
      <div className="image-viewer-modal__header">
        <h2 id="image-viewer-modal-title" className="profase-modal-title m-0 image-viewer-modal__title">
          <FaEye style={{ marginRight: 8, verticalAlign: '-2px' }} />
          {archivo?.nombre ?? 'Vista previa imagen'}
        </h2>
        <button type="button" className="profase-form-btn-secondary btn-sm" onClick={onClose}>
          Cerrar
        </button>
      </div>

      <div className="image-viewer-modal__body">
        {loading && (
          <div className="image-viewer-modal__status">
            <FaSpinner style={{ animation: 'spin 0.8s linear infinite' }} />
            Cargando imagen…
          </div>
        )}

        {!loading && error && (
          <div className="image-viewer-modal__error">{error}</div>
        )}

        {!loading && !error && imageUrl && (
          <img
            src={imageUrl}
            alt={archivo?.nombre ?? 'Imagen'}
            className="image-viewer-modal__image"
          />
        )}
      </div>
    </ProfaseModal>
  );
}
